import { z } from "zod";

import { apiClient } from "./api";

// `cost_usd` is serialized server-side as a string so JSON doesn't
// truncate the decimal precision. We keep it as a string here too —
// rendering uses Intl.NumberFormat to format; arithmetic stays on the
// backend where Decimal does not drift.
const decimalString = z.string();

export const usageSummarySchema = z.object({
  events: z.number().int(),
  prompt_tokens: z.number().int(),
  completion_tokens: z.number().int(),
  total_tokens: z.number().int(),
  searches: z.number().int(),
  cost_usd: decimalString,
});

export const breakdownDimensionSchema = z.enum(["model", "user", "slot", "provider", "agent"]);
export type BreakdownDimension = z.infer<typeof breakdownDimensionSchema>;

export const breakdownRowSchema = z.object({
  provider: z.string().nullable(),
  model_id: z.string().nullable(),
  slot: z.string().nullable(),
  actor_id: z.guid().nullable(),
  email: z.string().email().nullable(),
  name: z.string().nullable(),
  agent_id: z.guid().nullable(),
  // Resolved from the specialized-agent catalogue; both null when the agent
  // was deleted after its usage events were recorded.
  agent_name: z.string().nullable(),
  agent_slug: z.string().nullable(),
  events: z.number().int(),
  prompt_tokens: z.number().int(),
  completion_tokens: z.number().int(),
  total_tokens: z.number().int(),
  searches: z.number().int(),
  cost_usd: decimalString,
});
export const breakdownListSchema = z.array(breakdownRowSchema);

export const timeseriesIntervalSchema = z.enum(["hour", "day"]);
export type TimeseriesInterval = z.infer<typeof timeseriesIntervalSchema>;

export const timeseriesMetricSchema = z.enum(["cost", "tokens"]);
export type TimeseriesMetric = z.infer<typeof timeseriesMetricSchema>;

export const timeseriesPointSchema = z.object({
  bucket: z.string(),
  cost_usd: decimalString.nullable(),
  total_tokens: z.number().int().nullable(),
});
export const timeseriesListSchema = z.array(timeseriesPointSchema);

export const topDimensionSchema = z.enum(["user", "model"]);
export type TopDimension = z.infer<typeof topDimensionSchema>;

export const topMetricSchema = z.enum(["cost", "tokens"]);
export type TopMetric = z.infer<typeof topMetricSchema>;

export const topRowSchema = z.object({
  actor_id: z.guid().nullable(),
  email: z.string().email().nullable(),
  name: z.string().nullable(),
  provider: z.string().nullable(),
  model_id: z.string().nullable(),
  cost_usd: decimalString,
  total_tokens: z.number().int(),
});
export const topListSchema = z.array(topRowSchema);

export const usageEventSchema = z.object({
  id: z.guid(),
  created_at: z.string(),
  provider: z.string(),
  model_id: z.string(),
  slot: z.string(),
  operation: z.string(),
  actor_type: z.string(),
  actor_id: z.guid().nullable(),
  actor_email: z.string().email().nullable(),
  actor_name: z.string().nullable(),
  correlation_id: z.guid().nullable(),
  chat_session_id: z.guid().nullable(),
  chat_message_id: z.guid().nullable(),
  knowledge_item_id: z.guid().nullable(),
  prompt_tokens: z.number().int().nullable(),
  completion_tokens: z.number().int().nullable(),
  total_tokens: z.number().int().nullable(),
  searches: z.number().int().nullable(),
  cost_usd: decimalString.nullable(),
  cost_source: z.string().nullable(),
  latency_ms: z.number().int().nullable(),
  metadata: z.record(z.string(), z.unknown()),
});

export const usageEventsPageSchema = z.object({
  rows: z.array(usageEventSchema),
  total: z.number().int(),
  limit: z.number().int(),
  offset: z.number().int(),
});

export type UsageSummary = z.infer<typeof usageSummarySchema>;
export type BreakdownRow = z.infer<typeof breakdownRowSchema>;
export type TimeseriesPoint = z.infer<typeof timeseriesPointSchema>;
export type TopRow = z.infer<typeof topRowSchema>;
export type UsageEvent = z.infer<typeof usageEventSchema>;
export type UsageEventsPage = z.infer<typeof usageEventsPageSchema>;

export interface DateRange {
  from?: string;
  to?: string;
}

/** Last `days` days ending now, as a UTC ISO `DateRange`. Shared by every
 * admin surface with a day-count preset picker (usage, per-user activity)
 * so the preset-to-range math lives in one place. */
export function presetRange(days: number): { from: string; to: string } {
  const end = new Date();
  const start = new Date(end.getTime() - days * 24 * 60 * 60 * 1000);
  return { from: start.toISOString(), to: end.toISOString() };
}

// Exported so other lib modules with a `DateRange`-scoped endpoint (e.g.
// `lib/user-topics.ts`) reuse this instead of redeclaring it.
export const rangeQuery = (range: DateRange): Record<string, string | undefined> => ({
  from: range.from,
  to: range.to,
});

export const usageApi = {
  summary: (range: DateRange) =>
    apiClient.get("/admin/usage/summary", usageSummarySchema, {
      query: rangeQuery(range),
    }),
  breakdown: (range: DateRange, dimension: BreakdownDimension) =>
    apiClient.get("/admin/usage/breakdown", breakdownListSchema, {
      query: { ...rangeQuery(range), dimension },
    }),
  timeseries: (range: DateRange, interval: TimeseriesInterval, metric: TimeseriesMetric) =>
    apiClient.get("/admin/usage/timeseries", timeseriesListSchema, {
      query: { ...rangeQuery(range), interval, metric },
    }),
  top: (range: DateRange, dimension: TopDimension, metric: TopMetric, limit = 10) =>
    apiClient.get("/admin/usage/top", topListSchema, {
      query: { ...rangeQuery(range), dimension, metric, limit },
    }),
  events: (
    range: DateRange,
    filters: {
      user_id?: string;
      provider?: string;
      model_id?: string;
      slot?: string;
    },
    limit: number,
    offset: number,
  ) =>
    apiClient.get("/admin/usage/events", usageEventsPageSchema, {
      query: {
        ...rangeQuery(range),
        ...filters,
        limit,
        offset,
      },
    }),
};

export const SLOT_LABELS: Record<string, string> = {
  chat: "chat",
  embedding: "embedding",
  rerank: "rerank",
  reasoning_router: "reasoning router",
  entity_extraction: "entity extraction",
  chat_learning: "chat learning",
  query_keywords: "query keywords",
  image_description: "image description",
  audio_transcription: "audio transcription",
  voice_training: "voice training",
  dictation: "dictation",
  chunk_context: "chunk context",
};

// Whitelisted by the `usage_events.provider` CHECK constraint.
// Keep this list aligned with the backend CHECK constraint.
export const PROVIDER_OPTIONS = [
  "anthropic",
  "openai",
  "openrouter",
  "cohere",
  "voyage",
  "google",
  "typesafe",
  "external",
  "beflow",
] as const;

export const USAGE_PROVIDER_LABELS: Record<(typeof PROVIDER_OPTIONS)[number], string> = {
  anthropic: "Anthropic",
  openai: "OpenAI",
  openrouter: "OpenRouter",
  cohere: "Cohere",
  voyage: "Voyage",
  google: "Google",
  typesafe: "Typesafe (Jev)",
  external: "Modelo externo",
  beflow: "Wrapper BeFlow",
};

const currencyFormatter = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  minimumFractionDigits: 2,
  maximumFractionDigits: 6,
});

const budgetUsdFormatter = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const tokensFormatter = new Intl.NumberFormat("en-US");

export const formatCost = (value: string | null | undefined): string => {
  if (value === null || value === undefined) return "—";
  const parsed = Number.parseFloat(value);
  if (!Number.isFinite(parsed)) return value;
  return currencyFormatter.format(parsed);
};

// Budget amounts are whole-dollar caps shown in the chat usage panel; round to
// cents. Unlike `formatCost` (up to 6 digits, for tiny per-slot/per-model
// costs), a budget like "$7.75677 de $20.00" reads as broken — spend and cap
// are both dollar-scale, so 2 decimals is the right precision here.
export const formatBudgetUsd = (value: string | null | undefined): string => {
  if (value === null || value === undefined) return "—";
  const parsed = Number.parseFloat(value);
  if (!Number.isFinite(parsed)) return value;
  return budgetUsdFormatter.format(parsed);
};

export const formatTokens = (value: number | null | undefined): string => {
  if (value === null || value === undefined) return "—";
  return tokensFormatter.format(value);
};
